import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { api, API } from "@/lib/api";
import CandidatureModal from "@/components/CandidatureModal";
import FundingCalendarView, { FundingCallDetailPanel } from "@/components/FundingCalendarView";

/** Calendar of saved funding calls (CRUD, AI summary from URL, list + month view). */
const STATUSES = ["Por revisar", "Interesa", "Preparando", "Presentada", "Aprobada", "Denegada"];
const STATUS_COLORS = {
  "Por revisar": "bg-stone-200 text-stone-700",
  "Interesa": "bg-amber-200/70 text-amber-900",
  "Preparando": "bg-blue-200/70 text-blue-900",
  "Presentada": "bg-violet-200/70 text-violet-900",
  "Aprobada": "bg-emerald-200/70 text-emerald-900",
  "Denegada": "bg-red-200/70 text-red-900",
};
const STATUS_DOT = {
  "Por revisar": "bg-stone-500",
  "Interesa": "bg-amber-600",
  "Preparando": "bg-blue-600",
  "Presentada": "bg-violet-600",
  "Aprobada": "bg-emerald-600",
  "Denegada": "bg-red-600",
};

export default function SavedCallsTab() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => {
    try { return localStorage.getItem("funding:view") || "calendar"; } catch { return "calendar"; }
  });
  const [filterStatus, setFilterStatus] = useState("Todos");
  const [filterPhase, setFilterPhase] = useState("Todas");
  const [filterCountry, setFilterCountry] = useState("Todos");
  const [filterPriority, setFilterPriority] = useState("Todas");
  const [filterState2027, setFilterState2027] = useState("Todos");
  const [query, setQuery] = useState("");
  const [selectedCallId, setSelectedCallId] = useState(null);
  const [error, setError] = useState("");

  // Persistir vista preferida
  const changeView = (v) => {
    setView(v);
    try { localStorage.setItem("funding:view", v); } catch (_e) { /* ignore */ }
  };

  // Import form
  const [url, setUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState("");

  // Detail modal
  const [editing, setEditing] = useState(null);
  const [presentingCall, setPresentingCall] = useState(null);   // call para crear candidatura

  // Calendar month nav
  const [cursorMonth, setCursorMonth] = useState(() => {
    const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await api.get("/funding/saved");
      setCalls(r.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleImport = async (e) => {
    e?.preventDefault?.();
    if (!url.trim()) return;
    setImporting(true); setImportMsg(""); setError("");
    try {
      const r = await api.post("/funding/saved/import", { url: url.trim() });
      if (r.data.already_exists) setImportMsg("Esa convocatoria ya estaba guardada.");
      else setImportMsg(`✓ Importada: ${r.data.call.title}`);
      setUrl("");
      await load();
    } catch (e2) {
      setError(e2?.response?.data?.detail || e2.message);
    } finally { setImporting(false); setTimeout(() => setImportMsg(""), 4000); }
  };

  const filtered = useMemo(() => {
    return calls.filter((c) => {
      if (filterStatus !== "Todos" && c.status !== filterStatus) return false;
      if (filterPhase !== "Todas" && c.phase !== filterPhase) return false;
      if (filterCountry !== "Todos" && c.country_scope !== filterCountry) return false;
      if (filterPriority !== "Todas" && c.priority !== filterPriority) return false;
      if (filterState2027 !== "Todos" && c.state_2027 !== filterState2027) return false;
      if (query.trim()) {
        const q = query.trim().toLowerCase();
        const hay = [c.title, c.organism, c.summary, c.category, c.region, ...(c.tags || [])]
          .filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [calls, filterStatus, filterPhase, filterCountry, filterPriority, filterState2027, query]);

  const selectedCall = useMemo(
    () => filtered.find(c => c.id === selectedCallId) || calls.find(c => c.id === selectedCallId) || null,
    [filtered, calls, selectedCallId]
  );

  const changeStatus = async (call, newStatus) => {
    try {
      const r = await api.patch(`/funding/saved/${call.id}`, { status: newStatus });
      setCalls(prev => prev.map(x => x.id === call.id ? { ...x, ...r.data } : x));
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
  };

  const removeCall = async (c) => {
    if (!window.confirm(`¿Eliminar "${c.title}"?`)) return;
    try {
      await api.delete(`/funding/saved/${c.id}`);
      await load();
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
  };

  const onSaved = async (updated) => {
    setCalls((prev) => prev.map((x) => x.id === updated.id ? updated : x));
    setEditing(null);
  };

  return (
    <div data-testid="saved-calls-tab">
      <form onSubmit={handleImport} className="mb-6 rounded-2xl border border-stone-200 bg-white p-4 sm:p-5"
            data-testid="saved-import-form">
        <p className="text-[11px] uppercase tracking-[0.3em] text-[#26775E]">Añadir convocatoria</p>
        <div className="mt-2 flex flex-wrap gap-2 sm:flex-nowrap">
          <input type="url" placeholder="https://… link a la convocatoria"
                 value={url} onChange={(e) => setUrl(e.target.value)}
                 data-testid="saved-import-url"
                 className="flex-1 min-w-[260px] rounded-md border border-stone-300 px-3 py-2 text-sm outline-none focus:border-[#26775E]" />
          <button type="submit" disabled={importing || !url.trim()}
                  data-testid="saved-import-submit"
                  className="rounded-full bg-[#26775E] px-5 py-2 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#1c5c48] disabled:opacity-40">
            {importing ? "Resumiendo con IA…" : "+ Importar"}
          </button>
        </div>
        <p className="mt-2 text-[12px] text-stone-500">
          Pega un enlace a una convocatoria (ICAA, MEDIA, ICEC, festival…). La IA leerá la página y la añadirá con título, organismo, fechas, dotación y resumen.
        </p>
        {importMsg && <p className="mt-2 rounded bg-emerald-50 px-3 py-1.5 text-sm text-emerald-700" data-testid="saved-import-msg">{importMsg}</p>}
        {error && <p className="mt-2 rounded bg-red-50 px-3 py-1.5 text-sm text-red-700" data-testid="saved-error">{error}</p>}
      </form>

      <div className="mb-4 rounded-xl border border-stone-200 bg-white p-3">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <input placeholder="Buscar convocatoria…"
                 value={query} onChange={(e) => setQuery(e.target.value)}
                 data-testid="saved-filter-search"
                 className="rounded-md border border-stone-300 px-3 py-1.5 text-sm" />
          <select value={filterPhase} onChange={(e) => setFilterPhase(e.target.value)}
                  data-testid="saved-filter-phase"
                  className="rounded-md border border-stone-300 px-2 py-1.5 text-sm">
            <option value="Todas">Todas las fases</option>
            <option value="idea">💡 Idea</option>
            <option value="desarrollo">🎬 Desarrollo</option>
            <option value="produccion">🎥 Producción</option>
            <option value="distribucion">📢 Distribución</option>
            <option value="festivales">🎞 Festivales</option>
          </select>
          <select value={filterCountry} onChange={(e) => setFilterCountry(e.target.value)}
                  data-testid="saved-filter-country"
                  className="rounded-md border border-stone-300 px-2 py-1.5 text-sm">
            <option value="Todos">Todos los ámbitos</option>
            <option value="españa">España</option>
            <option value="madrid">Madrid</option>
            <option value="ue">Europa</option>
            <option value="iberoamerica">Iberoamérica</option>
          </select>
          <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}
                  data-testid="saved-filter-priority"
                  className="rounded-md border border-stone-300 px-2 py-1.5 text-sm">
            <option value="Todas">Toda prioridad</option>
            <option value="alta">Prioridad alta</option>
            <option value="media">Prioridad media</option>
            <option value="baja">Prioridad baja</option>
            <option value="especifica">Prioridad específica</option>
          </select>
          <select value={filterState2027} onChange={(e) => setFilterState2027(e.target.value)}
                  data-testid="saved-filter-state"
                  className="rounded-md border border-stone-300 px-2 py-1.5 text-sm">
            <option value="Todos">Todo estado 2027</option>
            <option value="confirmado">Confirmado</option>
            <option value="prevision">Previsión</option>
            <option value="por_determinar">Por determinar</option>
          </select>
          <div className="ml-auto inline-flex rounded-full border border-stone-300 bg-white p-1 text-[11px] uppercase tracking-[0.2em]">
            <button onClick={() => changeView("calendar")} data-testid="saved-view-calendar"
                    className={`rounded-full px-3 py-1 ${view === "calendar" ? "bg-stone-900 text-[#FAFAF6]" : "text-stone-600"}`}>Calendario</button>
            <button onClick={() => changeView("list")} data-testid="saved-view-list"
                    className={`rounded-full px-3 py-1 ${view === "list" ? "bg-stone-900 text-[#FAFAF6]" : "text-stone-600"}`}>Lista</button>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="saved-filter-bar">
          <FilterPill label={`Todos (${calls.length})`} active={filterStatus === "Todos"} onClick={() => setFilterStatus("Todos")} testid="filter-todos" />
          {STATUSES.map((s) => {
            const n = calls.filter((c) => c.status === s).length;
            if (!n) return null;
            return <FilterPill key={s} label={`${s} (${n})`} active={filterStatus === s} onClick={() => setFilterStatus(s)} testid={`filter-${s}`} />;
          })}
        </div>
      </div>

      {loading && <p className="text-sm text-stone-500">Cargando…</p>}
      {!loading && !calls.length && (
        <div className="rounded-xl border border-dashed border-stone-300 bg-stone-50 p-8 text-center text-sm text-stone-600">
          Aún no hay convocatorias guardadas. Pega un enlace arriba para empezar.
        </div>
      )}

      {view === "list" && filtered.length > 0 && (
        <ul className="space-y-3" data-testid="saved-list">
          {filtered.map((c) => (
            <li key={c.id} className="group rounded-xl border border-stone-200 bg-white p-4 transition hover:border-[#26775E]/40"
                data-testid={`saved-card-${c.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-stone-500">{c.region} · {c.category}</p>
                  <h3 className="mt-1 text-base font-medium text-stone-900">
                    <a href={c.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#26775E]" data-testid={`saved-link-${c.id}`}>
                      {c.title} <span className="text-stone-400">↗</span>
                    </a>
                  </h3>
                  {c.organism && <p className="text-[12px] text-stone-600">{c.organism}</p>}
                </div>
                <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest ${STATUS_COLORS[c.status] || STATUS_COLORS["Por revisar"]}`}>{c.status}</span>
              </div>

              <div className="mt-3 grid gap-2 text-[12px] text-stone-600 sm:grid-cols-3">
                <div><span className="uppercase tracking-widest text-stone-400">Apertura</span><br />{c.opens_at || "—"}</div>
                <div className="font-medium"><span className="uppercase tracking-widest text-stone-400 font-normal">Cierre</span><br />{c.closes_at || "—"} {c.closes_at && <DaysLeft date={c.closes_at} />}</div>
                <div><span className="uppercase tracking-widest text-stone-400">Dotación</span><br /><span className="line-clamp-1">{c.amount || "—"}</span></div>
              </div>

              {c.summary && <p className="mt-3 text-[13px] leading-snug text-stone-700">{c.summary}</p>}

              {c.tags?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {c.tags.map((t) => <span key={t} className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] uppercase tracking-widest text-stone-600">{t}</span>)}
                </div>
              )}

              {c.notes_internal && (
                <p className="mt-3 rounded bg-[#FBFAF4] px-3 py-2 text-[12px] italic text-stone-600">
                  <strong className="not-italic text-[#26775E]">Nota interna:</strong> {c.notes_internal}
                </p>
              )}

              <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
                <button onClick={() => setPresentingCall(c)} data-testid={`saved-present-${c.id}`}
                        className="rounded-full bg-gb-red px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-white hover-gb-red">
                  ✦ Presentar
                </button>
                <button onClick={() => setEditing(c)} data-testid={`saved-edit-${c.id}`}
                        className="rounded-full bg-stone-100 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-stone-700 hover:bg-stone-200">
                  Editar
                </button>
                <button onClick={() => removeCall(c)} data-testid={`saved-delete-${c.id}`}
                        className="rounded-full bg-stone-100 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-stone-700 hover:bg-red-100 hover:text-red-700">
                  Borrar
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {view === "calendar" && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr,380px]">
          <FundingCalendarView
            calls={filtered}
            selectedId={selectedCallId}
            onSelect={(c) => setSelectedCallId(c.id)}
          />
          <div className="lg:sticky lg:top-4 h-fit">
            <FundingCallDetailPanel
              call={selectedCall}
              onStatusChange={changeStatus}
              onPresent={(c) => setPresentingCall(c)}
              onEdit={(c) => setEditing(c)}
            />
          </div>
        </div>
      )}

      {editing && <EditCallModal call={editing} onClose={() => setEditing(null)} onSaved={onSaved} />}
      {presentingCall && (
        <CandidatureModal mode="create" initial={{ call_id: presentingCall.id }}
                          onClose={() => setPresentingCall(null)}
                          onSaved={() => setPresentingCall(null)} />
      )}
    </div>
  );
}

const FilterPill = ({ label, active, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
          className={`rounded-full px-3 py-1 text-[11px] uppercase tracking-widest transition ${active ? "bg-stone-900 text-[#FAFAF6]" : "bg-stone-200 text-stone-700 hover:bg-stone-300"}`}>
    {label}
  </button>
);

function DaysLeft({ date }) {
  const now = new Date();
  const target = new Date(date);
  const days = Math.ceil((target - now) / 86400000);
  if (isNaN(days)) return null;
  if (days < 0) return <span className="ml-1 text-red-600">(cerrada hace {Math.abs(days)} d)</span>;
  if (days === 0) return <span className="ml-1 text-red-600">(hoy)</span>;
  if (days <= 14) return <span className="ml-1 text-amber-700">({days} d)</span>;
  return <span className="ml-1 text-stone-500">({days} d)</span>;
}

// ---------------- Calendar month view ----------------
function CalendarMonth({ calls, cursor, setCursor, onClickCall }) {
  const monthName = cursor.toLocaleDateString("es-ES", { month: "long", year: "numeric" });
  const firstDayWeekIdx = (cursor.getDay() + 6) % 7;   // Mon=0
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();

  // Map close date → calls
  const callsByDay = useMemo(() => {
    const map = {};
    calls.forEach((c) => {
      if (!c.closes_at) return;
      const d = new Date(c.closes_at);
      if (d.getFullYear() === cursor.getFullYear() && d.getMonth() === cursor.getMonth()) {
        const key = d.getDate();
        (map[key] = map[key] || []).push(c);
      }
    });
    return map;
  }, [calls, cursor]);

  // Offset by archived/future months (we keep simple navigation; bottom list shows beyond-month items)
  const offMonthClosing = calls.filter((c) => c.closes_at).filter((c) => {
    const d = new Date(c.closes_at);
    return d.getFullYear() !== cursor.getFullYear() || d.getMonth() !== cursor.getMonth();
  });

  return (
    <div data-testid="saved-calendar">
      <div className="mb-3 flex items-center justify-between">
        <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
                data-testid="cal-prev"
                className="rounded-full bg-stone-200 px-3 py-1 text-[10px] uppercase tracking-widest hover:bg-stone-300">← Mes</button>
        <p className="font-display text-xl capitalize">{monthName}</p>
        <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
                data-testid="cal-next"
                className="rounded-full bg-stone-200 px-3 py-1 text-[10px] uppercase tracking-widest hover:bg-stone-300">Mes →</button>
      </div>

      <div className="grid grid-cols-7 gap-1 rounded-xl border border-stone-200 bg-white p-2">
        {["lun", "mar", "mié", "jue", "vie", "sáb", "dom"].map((d) => (
          <div key={d} className="px-1 py-1 text-center text-[10px] uppercase tracking-widest text-stone-500">{d}</div>
        ))}
        {Array.from({ length: firstDayWeekIdx }).map((_, i) => (
          <div key={`empty-${i}`} className="min-h-[72px]" />
        ))}
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
          const dayCalls = callsByDay[day] || [];
          const isToday = (() => {
            const t = new Date();
            return t.getFullYear() === cursor.getFullYear() && t.getMonth() === cursor.getMonth() && t.getDate() === day;
          })();
          return (
            <div key={day} className={`min-h-[72px] rounded-md border p-1 text-[10px] ${isToday ? "border-[#26775E] bg-[#26775E]/5" : "border-stone-100"}`}
                 data-testid={`cal-day-${day}`}>
              <p className={`text-right text-[11px] font-semibold ${isToday ? "text-[#26775E]" : "text-stone-500"}`}>{day}</p>
              {dayCalls.map((c) => (
                <button key={c.id} onClick={() => onClickCall(c)}
                        data-testid={`cal-call-${c.id}`}
                        className="mt-0.5 block w-full truncate rounded px-1 py-0.5 text-left text-[10px] hover:bg-stone-100"
                        title={`${c.title} · ${c.status}`}>
                  <span className={`mr-1 inline-block h-1.5 w-1.5 rounded-full align-middle ${STATUS_DOT[c.status] || STATUS_DOT["Por revisar"]}`} />
                  <span className="text-stone-700">{c.title.slice(0, 28)}{c.title.length > 28 ? "…" : ""}</span>
                </button>
              ))}
            </div>
          );
        })}
      </div>

      {offMonthClosing.length > 0 && (
        <p className="mt-3 text-[12px] text-stone-500">
          También hay {offMonthClosing.length} convocatorias con cierre en otros meses. Cambia de mes para verlas.
        </p>
      )}
    </div>
  );
}

// ---------------- Detail modal (with tabs) ----------------
function EditCallModal({ call, onClose, onSaved }) {
  const [data, setData] = useState({ ...call });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("datos");  // datos | documento | chat | conclusiones

  const upd = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.patch(`/funding/saved/${call.id}`, {
        title: data.title, organism: data.organism,
        opens_at: data.opens_at || null, closes_at: data.closes_at || null,
        amount: data.amount, category: data.category, region: data.region,
        requirements: data.requirements, summary: data.summary,
        tags: typeof data.tags === "string" ? data.tags.split(",").map((x) => x.trim()).filter(Boolean) : data.tags,
        status: data.status, notes_internal: data.notes_internal,
        conclusions: data.conclusions || "",
        next_steps: data.next_steps || [],
      });
      onSaved(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const refreshFromAI = async () => {
    if (!window.confirm("Volver a leer la URL y re-generar el resumen con IA. Sobrescribirá los datos extraídos pero conservará tu estado y notas. ¿Continuar?")) return;
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/funding/saved/${call.id}/refresh`);
      setData((prev) => ({ ...prev, ...r.data }));
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const TABS = [
    { id: "datos", label: "Datos" },
    { id: "documento", label: "Documento PDF" },
    { id: "chat", label: "Chat IA" },
    { id: "conclusiones", label: "Conclusiones" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-3 sm:p-6"
         onClick={onClose} data-testid="saved-edit-modal">
      <div className="my-6 flex w-full max-w-4xl flex-col rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 border-b border-stone-200 px-5 py-4">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.35em] text-stone-500">Convocatoria</p>
            <h2 className="mt-0.5 truncate font-display text-lg text-stone-900">{data.title || "(sin título)"}</h2>
            <p className="mt-0.5 text-[11px] text-stone-500">{data.organism} {data.closes_at && `· cierre ${data.closes_at}`}</p>
          </div>
          <button onClick={onClose} data-testid="modal-close"
                  className="rounded-full px-2 py-1 text-stone-500 hover:bg-stone-100 hover:text-stone-900">✕</button>
        </div>

        {/* Tabs */}
        <div className="flex shrink-0 gap-1 overflow-x-auto border-b border-stone-200 px-3 pb-0 pt-2">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`shrink-0 rounded-t-lg px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] transition ${
                tab === t.id
                  ? "bg-stone-900 text-white"
                  : "text-stone-500 hover:bg-stone-100 hover:text-stone-900"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="min-h-[420px] px-5 pb-5 pt-4">
          {tab === "datos" && <DatosTab data={data} upd={upd} />}
          {tab === "documento" && (
            <DocumentoTab callId={call.id} data={data} onUpdated={(meta) => setData((d) => ({ ...d, ...meta }))} />
          )}
          {tab === "chat" && (
            <ChatTab callId={call.id} hasDocument={!!data.document_filename} />
          )}
          {tab === "conclusiones" && (
            <ConclusionesTab callId={call.id} data={data} upd={upd} />
          )}
        </div>

        {err && <p className="mx-5 mb-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}

        <div className="flex flex-wrap items-center justify-between gap-2 rounded-b-2xl border-t border-stone-200 bg-stone-50 px-5 py-3">
          <button onClick={refreshFromAI} disabled={busy} data-testid="edit-refresh-ai"
                  className="rounded-full bg-stone-100 px-3 py-2 text-[11px] uppercase tracking-widest text-stone-700 hover:bg-stone-200 disabled:opacity-40">
            ↻ Re-extraer URL con IA
          </button>
          <div className="ml-auto flex gap-2">
            <button onClick={onClose}
                    className="rounded-full bg-stone-200 px-4 py-2 text-[11px] uppercase tracking-widest text-stone-700 hover:bg-stone-300">
              Cancelar
            </button>
            <button onClick={save} disabled={busy} data-testid="edit-save"
                    className="rounded-full bg-[#26775E] px-5 py-2 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#1c5c48] disabled:opacity-40">
              {busy ? "Guardando…" : "Guardar cambios"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------- Tab: Datos (form fields) ----------------
function DatosTab({ data, upd }) {
  return (
    <div>
      <label className="block text-sm">
        <span className="text-[11px] uppercase tracking-[0.3em] text-stone-500">Título</span>
        <input value={data.title || ""} onChange={(e) => upd("title", e.target.value)} data-testid="edit-title"
               className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 outline-none focus:border-[#26775E]" />
      </label>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-sm"><span className="text-[11px] uppercase tracking-[0.3em] text-stone-500">Organismo</span>
          <input value={data.organism || ""} onChange={(e) => upd("organism", e.target.value)}
                 className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 outline-none focus:border-[#26775E]" />
        </label>
        <label className="text-sm"><span className="text-[11px] uppercase tracking-[0.3em] text-stone-500">Estado</span>
          <select value={data.status} onChange={(e) => upd("status", e.target.value)} data-testid="edit-status"
                  className="mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 outline-none focus:border-[#26775E]">
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </label>
        <label className="text-sm"><span className="text-[11px] uppercase tracking-[0.3em] text-stone-500">Apertura</span>
          <input type="date" value={data.opens_at || ""} onChange={(e) => upd("opens_at", e.target.value)}
                 className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 outline-none focus:border-[#26775E]" />
        </label>
        <label className="text-sm"><span className="text-[11px] uppercase tracking-[0.3em] text-stone-500">Cierre</span>
          <input type="date" value={data.closes_at || ""} onChange={(e) => upd("closes_at", e.target.value)} data-testid="edit-closes-at"
                 className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 outline-none focus:border-[#26775E]" />
        </label>
        <label className="text-sm"><span className="text-[11px] uppercase tracking-[0.3em] text-stone-500">Región</span>
          <select value={data.region} onChange={(e) => upd("region", e.target.value)}
                  className="mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 outline-none focus:border-[#26775E]">
            {["Spain national", "CCAA", "Europe", "International", "Festival"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </label>
        <label className="text-sm"><span className="text-[11px] uppercase tracking-[0.3em] text-stone-500">Categoría</span>
          <select value={data.category} onChange={(e) => upd("category", e.target.value)}
                  className="mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 outline-none focus:border-[#26775E]">
            {["Desarrollo", "Producción", "Distribución", "Festival", "Coproducción", "Formación", "Talento", "Otro"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </label>
      </div>

      <label className="mt-3 block text-sm"><span className="text-[11px] uppercase tracking-[0.3em] text-stone-500">Dotación</span>
        <input value={data.amount || ""} onChange={(e) => upd("amount", e.target.value)}
               className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 outline-none focus:border-[#26775E]" />
      </label>
      <label className="mt-3 block text-sm"><span className="text-[11px] uppercase tracking-[0.3em] text-stone-500">Etiquetas (separadas por coma)</span>
        <input value={Array.isArray(data.tags) ? data.tags.join(", ") : data.tags || ""}
               onChange={(e) => upd("tags", e.target.value)}
               className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 outline-none focus:border-[#26775E]" />
      </label>
      <label className="mt-3 block text-sm"><span className="text-[11px] uppercase tracking-[0.3em] text-stone-500">Resumen</span>
        <textarea rows={4} value={data.summary || ""} onChange={(e) => upd("summary", e.target.value)}
                  className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 outline-none focus:border-[#26775E]" />
      </label>
      <label className="mt-3 block text-sm"><span className="text-[11px] uppercase tracking-[0.3em] text-stone-500">Requisitos</span>
        <textarea rows={3} value={data.requirements || ""} onChange={(e) => upd("requirements", e.target.value)}
                  className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 outline-none focus:border-[#26775E]" />
      </label>
      <label className="mt-3 block text-sm"><span className="text-[11px] uppercase tracking-[0.3em] text-stone-500">Nota interna (visible para todo el equipo)</span>
        <textarea rows={2} value={data.notes_internal || ""} onChange={(e) => upd("notes_internal", e.target.value)} data-testid="edit-notes"
                  className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 outline-none focus:border-[#26775E]" />
      </label>

      <p className="mt-3 text-[11px] text-stone-500">Fuente: <a href={data.url} target="_blank" rel="noopener noreferrer" className="text-[#26775E] hover:underline">{data.url}</a></p>
    </div>
  );
}

// ---------------- Tab: Documento (upload PDF + viewer) ----------------
function DocumentoTab({ callId, data, onUpdated }) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const [viewing, setViewing] = useState(false);
  const fileInputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true); setErr("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/funding/saved/${callId}/document`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      onUpdated({
        document_filename: r.data.filename,
        document_size: r.data.size,
        document_char_count: r.data.char_count,
        document_uploaded_at: r.data.uploaded_at,
      });
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("¿Eliminar el documento subido? Esto borrará también el contexto del chat IA.")) return;
    try {
      await api.delete(`/funding/saved/${callId}/document`);
      onUpdated({
        document_filename: null,
        document_size: null,
        document_char_count: null,
        document_uploaded_at: null,
      });
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  };

  const fileUrl = `${API}/funding/saved/${callId}/document/file`;
  const fakeDoc = {
    id: "current",
    title: "Documento oficial",
    filename: data.document_filename,
    mime: data.document_mime || "application/pdf",
    size: data.document_size,
  };

  return (
    <div data-testid="documento-tab">
      <div className="mb-4 rounded-lg border border-stone-200 bg-stone-50 p-4">
        <p className="text-[11px] uppercase tracking-[0.3em] text-stone-500">Documento oficial de la convocatoria</p>
        <p className="mt-1 text-[13px] text-stone-700">
          Sube aquí el BOE, resolución o bases oficiales en PDF. La IA del chat lo leerá completo y podrás
          preguntarle sobre plazos, requisitos, beneficiarios, gastos elegibles, etc.
        </p>
      </div>

      {!data.document_filename && (
        <div className="rounded-lg border-2 border-dashed border-stone-300 bg-white p-8 text-center">
          <p className="font-display text-lg text-stone-700">Aún no has subido el documento</p>
          <p className="mt-1 text-[12px] text-stone-500">PDF (recomendado), TXT o Markdown · máx. 25 MB</p>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="doc-upload-btn"
            className="mt-4 rounded-full bg-[#26775E] px-5 py-2 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#1c5c48] disabled:opacity-50"
          >
            {uploading ? "Subiendo…" : "+ Subir documento"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.txt,.md"
            className="hidden"
            data-testid="doc-upload-input"
            onChange={(e) => upload(e.target.files?.[0])}
          />
        </div>
      )}

      {data.document_filename && (
        <div className="rounded-lg border border-stone-200 bg-white p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#26775E]">PDF cargado</p>
              <p className="mt-0.5 truncate font-serif text-[15px] text-stone-900">{data.document_filename}</p>
              <p className="mt-1 text-[11px] text-stone-500">
                {data.document_size ? `${(data.document_size / 1024).toFixed(1)} KB` : "—"}
                {data.document_char_count > 0 && <> · {data.document_char_count.toLocaleString("es-ES")} caracteres extraídos</>}
                {data.document_uploaded_at && <> · {new Date(data.document_uploaded_at).toLocaleString("es-ES")}</>}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <button onClick={() => setViewing(true)} data-testid="doc-view-btn"
                      className="rounded-full bg-[#26775E] px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-[#1c5c48]">
                Ver
              </button>
              <a href={fileUrl} target="_blank" rel="noreferrer" download={data.document_filename}
                 className="rounded-full border border-stone-300 px-3 py-1.5 text-[11px] font-semibold text-stone-700 hover:border-[#26775E] hover:text-[#26775E]">
                Descargar
              </a>
              <button onClick={remove} data-testid="doc-remove-btn"
                      title="Eliminar"
                      className="rounded-full border border-stone-300 px-2.5 py-1.5 text-[11px] text-stone-500 hover:border-red-400 hover:text-red-600">
                ×
              </button>
            </div>
          </div>
          <p className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-[12px] text-emerald-800">
            ✓ El chat IA puede leer este documento. Ve a la pestaña <strong>Chat IA</strong> para preguntar.
          </p>
        </div>
      )}

      {err && <p className="mt-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}

      {viewing && (
        <DocumentViewerForFunding
          callId={callId}
          doc={fakeDoc}
          onClose={() => setViewing(false)}
        />
      )}
    </div>
  );
}

/** Wrapper para reutilizar DocumentViewer con la ruta de funding. */
function DocumentViewerForFunding({ callId, doc, onClose }) {
  // DocumentViewer espera projectId+documentId con ruta /icaa/projects/.../documents/.../file
  // pero aquí la ruta es /funding/saved/{callId}/document/file. Hacemos un mini-viewer ad-hoc inline.
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let created = null;
    (async () => {
      try {
        const r = await api.get(`/funding/saved/${callId}/document/file`, { responseType: "blob" });
        if (cancelled) return;
        const blob = new Blob([r.data], { type: doc.mime || "application/pdf" });
        created = URL.createObjectURL(blob);
        setBlobUrl(created);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; if (created) URL.revokeObjectURL(created); };
  }, [callId, doc.mime]);

  // Render a tiny inline overlay with iframe-style PDF if we have a URL.
  // We use a direct iframe for the funding doc (lighter than react-pdf for a quick look).
  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-[#2C2B29]" data-testid="funding-doc-viewer">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 bg-[#2C2B29] px-4 py-3 text-white sm:px-6">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.35em] text-[#941620]">{doc.title}</p>
          <p className="mt-0.5 truncate text-sm font-semibold">{doc.filename}</p>
        </div>
        <div className="flex items-center gap-2">
          {blobUrl && (
            <a href={blobUrl} download={doc.filename}
               className="rounded-full bg-[#26775E] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#1c5c48]">
              ↓ Descargar
            </a>
          )}
          <button onClick={onClose} data-testid="funding-doc-viewer-close"
                  className="rounded-full bg-white/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-white/20">
            ✕ Cerrar
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-auto bg-[#33333f]">
        {loading && <p className="p-6 text-center text-sm text-white/70">Cargando documento…</p>}
        {err && <p className="m-6 rounded bg-red-500/20 px-4 py-3 text-sm text-red-100">{err}</p>}
        {!loading && !err && blobUrl && (
          <iframe
            src={blobUrl}
            title={doc.filename}
            className="h-full w-full border-0"
            data-testid="funding-doc-iframe"
          />
        )}
      </div>
    </div>
  );
}

// ---------------- Tab: Chat IA ----------------
function ChatTab({ callId, hasDocument }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 9e9, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed) return;
    const next = [...messages, { role: "user", content: trimmed }];
    setMessages(next);
    setInput("");
    setError("");
    setLoading(true);
    try {
      const r = await api.post(`/funding/saved/${callId}/ai/chat`, { messages: next }, { timeout: 180000 });
      setMessages([...next, { role: "assistant", content: r.data.reply || "" }]);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Error de IA");
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); }
  };

  const QUICK = [
    "¿Cuáles son los plazos exactos de presentación?",
    "¿Quiénes pueden ser beneficiarios?",
    "¿Qué importes y porcentajes contempla?",
    "¿Qué documentación hay que aportar?",
    "¿Cuáles son los criterios de valoración?",
    "Hazme un resumen de los próximos pasos para preparar la solicitud.",
  ];

  return (
    <div className="flex flex-col" data-testid="chat-tab" style={{ minHeight: 380 }}>
      {!hasDocument && (
        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
          ⚠️ Aún no has subido el documento oficial. La IA responderá solo con el resumen previo (puede ser limitado).
          Para respuestas precisas, sube el PDF en la pestaña <strong>Documento PDF</strong>.
        </div>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto rounded-lg bg-stone-50 p-3"
           style={{ maxHeight: 360 }} data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="px-2 py-4 text-center">
            <p className="font-display text-base text-stone-700">Pregúntale a la IA sobre esta convocatoria</p>
            <p className="mt-1 text-[12px] text-stone-500">Plazos, requisitos, importes, documentación, criterios…</p>
            <div className="mt-4 flex flex-wrap justify-center gap-1.5" data-testid="chat-quick">
              {QUICK.map((q) => (
                <button key={q} onClick={() => send(q)}
                        className="rounded-full border border-stone-300 bg-white px-3 py-1 text-[11px] text-stone-700 hover:border-[#26775E] hover:text-[#26775E]">
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`mb-2 flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[88%] rounded-xl px-3 py-2 text-[13.5px] leading-relaxed whitespace-pre-wrap ${
              m.role === "user"
                ? "bg-stone-900 text-white"
                : "bg-white text-stone-800 shadow-sm"
            }`}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="rounded-xl bg-white px-3 py-2 text-[12px] text-stone-500 shadow-sm">
              Leyendo el documento…
            </div>
          </div>
        )}
      </div>

      {error && <p className="mt-2 rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

      <div className="mt-3 flex gap-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Escribe tu pregunta… (Cmd/Ctrl+Enter para enviar)"
          rows={2}
          data-testid="chat-input"
          className="flex-1 resize-none rounded-md border border-stone-300 px-3 py-2 text-sm outline-none focus:border-[#26775E]"
        />
        <button onClick={() => send()} disabled={loading || !input.trim()} data-testid="chat-send"
                className="rounded-full bg-[#26775E] px-4 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#1c5c48] disabled:opacity-40">
          Enviar
        </button>
      </div>
    </div>
  );
}

// ---------------- Tab: Conclusiones + siguientes pasos ----------------
function ConclusionesTab({ callId, data, upd }) {
  const nextSteps = Array.isArray(data.next_steps) ? data.next_steps : [];
  const [newStep, setNewStep] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);  // [{label, rationale}]
  const [suggestionErr, setSuggestionErr] = useState("");

  const addStep = (label) => {
    const lbl = (label || "").trim();
    if (!lbl) return;
    upd("next_steps", [
      ...nextSteps,
      { id: Math.random().toString(36).slice(2, 10), label: lbl, done: false, created_at: new Date().toISOString() },
    ]);
  };

  const addNewFromInput = () => {
    addStep(newStep);
    setNewStep("");
  };

  const toggleStep = (id) => {
    upd("next_steps", nextSteps.map((s) => s.id === id ? { ...s, done: !s.done } : s));
  };

  const removeStep = (id) => {
    upd("next_steps", nextSteps.filter((s) => s.id !== id));
  };

  const requestSuggestions = async () => {
    setSuggesting(true); setSuggestionErr(""); setSuggestions([]);
    try {
      const r = await api.post(`/funding/saved/${callId}/ai/suggest-steps`, {}, { timeout: 180000 });
      setSuggestions(r.data.steps || []);
      if (!r.data.steps?.length) setSuggestionErr("La IA no devolvió sugerencias. Prueba a guardar antes tus conclusiones.");
    } catch (e) {
      setSuggestionErr(e?.response?.data?.detail || e.message || "Error de IA");
    } finally {
      setSuggesting(false);
    }
  };

  const addOneSuggestion = (s) => {
    addStep(s.label);
    setSuggestions((cur) => cur.filter((x) => x.label !== s.label));
  };

  const addAllSuggestions = () => {
    const fresh = suggestions.map((s) => ({
      id: Math.random().toString(36).slice(2, 10),
      label: s.label,
      done: false,
      created_at: new Date().toISOString(),
    }));
    upd("next_steps", [...nextSteps, ...fresh]);
    setSuggestions([]);
  };

  return (
    <div data-testid="conclusiones-tab">
      <label className="block">
        <span className="text-[11px] uppercase tracking-[0.3em] text-stone-500">Conclusiones (resumen ejecutivo, riesgos, oportunidad)</span>
        <textarea
          rows={6}
          value={data.conclusions || ""}
          onChange={(e) => upd("conclusions", e.target.value)}
          data-testid="conclusions-text"
          placeholder="Anota aquí tus conclusiones tras leer el documento: ¿Encaja tu proyecto? ¿Riesgos? ¿Oportunidad real?"
          className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 text-sm outline-none focus:border-[#26775E]"
        />
      </label>

      <div className="mt-5">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-stone-500">Siguientes pasos</p>
            <p className="text-[11px] text-stone-500">Tareas concretas a realizar para presentarse. Marca cuando estén hechas.</p>
          </div>
          <button
            onClick={requestSuggestions}
            disabled={suggesting}
            data-testid="suggest-steps-btn"
            title="La IA leerá el documento + tus conclusiones y sugerirá pasos que te faltan"
            className="rounded-full bg-[#26775E] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-white hover:bg-[#1c5c48] disabled:opacity-50"
          >
            {suggesting ? "Pensando…" : "💡 Sugerir con IA"}
          </button>
        </div>

        {(suggestions.length > 0 || suggestionErr) && (
          <div className="mt-3 rounded-lg border border-[#941620]/40 bg-[#941620]/10 p-3" data-testid="suggestions-box">
            {suggestionErr && <p className="text-[12px] text-red-700">{suggestionErr}</p>}
            {suggestions.length > 0 && (
              <>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#a96f15]">
                    Sugerencias IA · {suggestions.length}
                  </p>
                  <div className="flex gap-1.5">
                    <button onClick={addAllSuggestions} data-testid="suggest-add-all"
                            className="rounded-full bg-stone-900 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-white hover:bg-stone-700">
                      + Añadir todas
                    </button>
                    <button onClick={() => setSuggestions([])}
                            className="rounded-full border border-stone-300 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-stone-600 hover:border-stone-500">
                      Descartar
                    </button>
                  </div>
                </div>
                <ul className="mt-2 space-y-1.5">
                  {suggestions.map((s, i) => (
                    <li key={i} data-testid={`suggestion-${i}`}
                        className="flex items-start gap-2 rounded-md border border-amber-200 bg-white px-3 py-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-serif text-[13.5px] leading-tight text-stone-900">{s.label}</p>
                        {s.rationale && (
                          <p className="mt-0.5 text-[11px] italic text-stone-600">↳ {s.rationale}</p>
                        )}
                      </div>
                      <button
                        onClick={() => addOneSuggestion(s)}
                        data-testid={`suggest-add-${i}`}
                        title="Añadir este paso"
                        className="shrink-0 rounded-full bg-[#26775E] px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-white hover:bg-[#1c5c48]"
                      >
                        + Añadir
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}

        <ul className="mt-3 space-y-1.5" data-testid="next-steps-list">
          {nextSteps.length === 0 && (
            <li className="rounded-md border border-dashed border-stone-300 bg-stone-50 px-3 py-3 text-center text-[12px] text-stone-500">
              Aún no hay pasos. Añade el primero abajo o pulsa <strong>💡 Sugerir con IA</strong>.
            </li>
          )}
          {nextSteps.map((s) => (
            <li key={s.id} data-testid={`step-${s.id}`}
                className={`flex items-start gap-2 rounded-md border border-stone-200 bg-white px-3 py-2 ${s.done ? "opacity-60" : ""}`}>
              <input type="checkbox" checked={!!s.done} onChange={() => toggleStep(s.id)}
                     data-testid={`step-check-${s.id}`}
                     className="mt-0.5 h-4 w-4 cursor-pointer accent-[#26775E]" />
              <span className={`flex-1 text-[13.5px] leading-snug ${s.done ? "line-through text-stone-500" : "text-stone-800"}`}>
                {s.label}
              </span>
              <button onClick={() => removeStep(s.id)} title="Eliminar"
                      data-testid={`step-del-${s.id}`}
                      className="rounded-full px-2 text-[11px] text-stone-400 hover:text-red-600">
                ×
              </button>
            </li>
          ))}
        </ul>

        <div className="mt-3 flex gap-2">
          <input
            value={newStep}
            onChange={(e) => setNewStep(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNewFromInput(); } }}
            placeholder='Ej. "Solicitar certificado AEAT y SS"'
            data-testid="step-input"
            className="flex-1 rounded-md border border-stone-300 px-3 py-2 text-sm outline-none focus:border-[#26775E]"
          />
          <button onClick={addNewFromInput} data-testid="step-add"
                  className="rounded-full bg-stone-900 px-4 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-stone-700">
            + Añadir
          </button>
        </div>
      </div>
    </div>
  );
}

